import { useState, useCallback } from 'react'
import { X, Lightbulb, ChevronRight, HelpCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

export interface QuickHelpItem {
    id: string
    title: string
    description: string
    /** Optional action when clicking the hint */
    action?: () => void
    /** Optional tour selector to highlight */
    tourSelector?: string
    /** Variant for styling */
    variant?: 'info' | 'tip' | 'warning'
}

export interface QuickHelpProps {
    /** Template name for determining which hints to show */
    templateName: string
    /** Custom hints to show (overrides template defaults) */
    hints?: QuickHelpItem[]
    /** Callback when a hint action is triggered */
    onAction?: (hintId: string) => void
    /** Additional className */
    className?: string
    /** Whether to allow dismissing hints */
    dismissible?: boolean
}

/**
 * Template-specific quick help hints configuration
 * Keys match template names from ProductTemplate/template.ts (e.g., 'Cloud.VCloud', 'Server.VBM')
 */
const TEMPLATE_HINTS: Record<string, QuickHelpItem[]> = {
    // Cloud products
    'cloud.vcloud': [
        {
            id: 'vcloud-custom',
            title: 'Bạn muốn mua cấu hình Custom?',
            description:
                'Chọn "Tùy chỉnh quota" để tự nhập vCPU, RAM theo nhu cầu',
            tourSelector: '[data-tour="vcloud-package-type"]',
            variant: 'tip',
        },
        {
            id: 'vcloud-multi-vm',
            title: 'Thêm nhiều loại VM?',
            description: 'Click nút "Thêm VM" để thêm nhiều gói VM khác nhau',
            tourSelector: '[data-tour="vcloud-instances"]',
            variant: 'info',
        },
    ],
    'cloud.vps': [
        {
            id: 'vps-config',
            title: 'Cấu hình VPS',
            description: 'Chọn gói VPS phù hợp với nhu cầu sử dụng',
            variant: 'info',
        },
    ],
    // Server products (Bare Metal)
    'server.vbm': [
        {
            id: 'server-pricing',
            title: 'Mô hình tính giá Server',
            description:
                'Package = 1 giá cố định cho cả server | Bundle = tổng từ từng thiết bị',
            tourSelector: '[data-tour="pricing-model"]',
            variant: 'tip',
        },
        {
            id: 'server-config',
            title: 'Cấu hình từ kho thiết bị',
            description:
                'Chọn Model server → Auto điền CPU, nhà sản xuất từ warehouse',
            tourSelector: '[data-tour="server-config"]',
            variant: 'info',
        },
    ],
    'server.colocation': [
        {
            id: 'colo-config',
            title: 'Dịch vụ Colocation',
            description: 'Khách hàng mang thiết bị riêng, thuê chỗ đặt tại DC',
            variant: 'info',
        },
    ],
    // CDN products
    'swiftfederation.cdn': [
        {
            id: 'cdn-commitment',
            title: 'Cấu hình cam kết bandwidth',
            description:
                'Dung lượng × Đơn giá = Tổng tiền cam kết. Thời gian cam kết quyết định cách đối soát',
            tourSelector: '[data-tour="bandwidth-commitment-value"]',
            variant: 'tip',
        },
        {
            id: 'cdn-overage',
            title: 'Đơn giá đối soát vượt?',
            description:
                'Cấu hình bảng giá lũy tiến/bậc thang cho phần vượt cam kết',
            tourSelector: '[data-tour="bandwidth-overage-pricing"]',
            variant: 'info',
        },
        {
            id: 'cdn-merge',
            title: 'Gộp bandwidth?',
            description:
                'Click "Gộp BW" để tính chung cho tất cả regions thành 1 global',
            tourSelector: '[data-tour="cdn-merge"]',
            variant: 'info',
        },
    ],
    // Transcoding products
    'swiftfederation.transcoding': [
        {
            id: 'transcoding-hours',
            title: 'Tính theo giờ transcoding',
            description: 'Nhập số giờ cam kết × đơn giá/giờ = thành tiền',
            tourSelector: '[data-tour="config-bandwidth"]',
            variant: 'info',
        },
    ],
    'swiftfederation.recording': [
        {
            id: 'recording-storage',
            title: 'Dung lượng Recording',
            description: 'Tính theo GB storage sử dụng cho lưu trữ recording',
            variant: 'info',
        },
    ],
    // SSL products
    'ssl.ssl': [
        {
            id: 'ssl-type',
            title: 'Chọn loại SSL phù hợp',
            description:
                'DV = xác thực domain | OV = xác thực tổ chức | EV = xác thực mở rộng',
            variant: 'tip',
        },
    ],
    // Domain products
    'domain.tlds': [
        {
            id: 'domain-tlds',
            title: 'Domain quốc tế (TLDs)',
            description: 'Các đuôi domain: .com, .net, .org, .io, ...',
            variant: 'info',
        },
    ],
    'domain.vnnic': [
        {
            id: 'domain-vnnic',
            title: 'Domain Việt Nam (VNNIC)',
            description: 'Các đuôi domain: .vn, .com.vn, .net.vn, ...',
            variant: 'info',
        },
        {
            id: 'vnnic-steps',
            title: 'Cần điền 4 bước thông tin',
            description:
                'VNNIC yêu cầu: Thông tin KH → Bản khai → Quản lý → Cài đặt',
            tourSelector: '[data-tour="vnnic-steps"]',
            variant: 'tip',
        },
    ],
    // Email products
    'email.server': [
        {
            id: 'email-server',
            title: 'Email Server',
            description: 'Cấu hình email server theo số lượng mailbox',
            variant: 'info',
        },
    ],
}

// SVG border animation component
const BorderAnimation = ({ isHovered }: { isHovered: boolean }) => (
    <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: -1 }}
    >
        <rect
            x="1"
            y="1"
            width="calc(100% - 2px)"
            height="calc(100% - 2px)"
            rx="6"
            ry="6"
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="2"
            strokeDasharray="1000"
            strokeDashoffset={isHovered ? '0' : '1000'}
            style={{
                transition: 'stroke-dashoffset 1.5s ease-in-out',
            }}
        />
    </svg>
)

const VARIANT_STYLES = {
    info: {
        bg: 'bg-slate-50',
        border: 'border-slate-300',
        icon: 'text-slate-500',
        title: 'text-slate-700',
        desc: 'text-slate-600',
    },
    tip: {
        bg: 'bg-slate-50',
        border: 'border-primary',
        icon: 'text-primary',
        title: 'text-slate-700',
        desc: 'text-slate-600',
    },
    warning: {
        bg: 'bg-red-50',
        border: 'border-red-300',
        icon: 'text-red-500',
        title: 'text-red-700',
        desc: 'text-red-600',
    },
}

/**
 * QuickHelp - Template-specific contextual help hints
 * Shows relevant tips and actions based on the current product template
 */
const QuickHelp = ({
    templateName,
    hints: customHints,
    onAction,
    className,
    dismissible = true,
}: QuickHelpProps) => {
    const [dismissedHints, setDismissedHints] = useState<Set<string>>(new Set())
    const [hoveredHintId, setHoveredHintId] = useState<string | null>(null)

    // Get hints for current template
    const templateKey = templateName?.toLowerCase() || ''
    const hints = customHints || TEMPLATE_HINTS[templateKey] || []

    // Filter out dismissed hints
    const visibleHints = hints.filter((h) => !dismissedHints.has(h.id))

    const handleDismiss = useCallback((hintId: string) => {
        setDismissedHints((prev) => new Set([...prev, hintId]))
    }, [])

    const handleHintClick = useCallback(
        (hint: QuickHelpItem) => {
            // Trigger action callback
            if (onAction) {
                onAction(hint.id)
            }

            // Execute hint action if defined
            if (hint.action) {
                hint.action()
            }

            // Scroll to and highlight tour selector if defined
            if (hint.tourSelector) {
                const element = document.querySelector(
                    hint.tourSelector
                ) as HTMLElement
                if (element) {
                    // Use tour-styles for consistent highlighting
                    injectTourStyles()
                    cleanupTourElements()
                    createTourOverlay()

                    element.classList.add('tour-highlight-element')
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    })

                    // Show popover after scroll
                    setTimeout(() => {
                        const popover = createTourPopover({
                            title: hint.title,
                            description: hint.description,
                            currentStep: 1,
                            totalSteps: 1,
                            onNext: () => cleanupTourElements(),
                            onClose: () => cleanupTourElements(),
                            isLastStep: true,
                        })

                        document.body.appendChild(popover)
                        positionPopover(popover, element)
                    }, 300)
                }
            }
        },
        [onAction]
    )

    if (visibleHints.length === 0) {
        return null
    }

    return (
        <div className={cn('space-y-2', className)}>
            {visibleHints.map((hint) => {
                const variant = hint.variant || 'info'
                const styles = VARIANT_STYLES[variant]

                return (
                    <div
                        key={hint.id}
                        className={cn(
                            'group relative rounded-md p-3 shadow-sm cursor-pointer overflow-hidden',
                            'transition-shadow duration-500',
                            'hover:shadow-md',
                            styles.bg
                        )}
                        onClick={() => handleHintClick(hint)}
                        onMouseEnter={() => setHoveredHintId(hint.id)}
                        onMouseLeave={() => setHoveredHintId(null)}
                    >
                        {/* SVG border animation */}
                        <BorderAnimation
                            isHovered={hoveredHintId === hint.id}
                        />

                        {/* Dismiss button */}
                        {dismissible && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleDismiss(hint.id)
                                }}
                                className={cn(
                                    'absolute top-2 right-2 p-1 rounded-full',
                                    'hover:bg-black/5 transition-colors',
                                    styles.icon
                                )}
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        )}

                        <div className="flex items-start gap-3 pr-6">
                            {/* Icon */}
                            <div className="flex-shrink-0 mt-0.5">
                                {variant === 'tip' ? (
                                    <Lightbulb
                                        className={cn('h-5 w-5', styles.icon)}
                                    />
                                ) : (
                                    <HelpCircle
                                        className={cn('h-5 w-5', styles.icon)}
                                    />
                                )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                                <p
                                    className={cn(
                                        'text-sm font-medium',
                                        styles.title
                                    )}
                                >
                                    {hint.title}
                                </p>
                                <p
                                    className={cn(
                                        'text-xs mt-0.5',
                                        styles.desc
                                    )}
                                >
                                    {hint.description}
                                </p>
                            </div>

                            {/* Arrow indicator */}
                            {hint.tourSelector && (
                                <div
                                    className={cn(
                                        'flex-shrink-0 self-center',
                                        styles.icon
                                    )}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </div>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default QuickHelp

/**
 * Get template hints by template name
 */
export const getTemplateHints = (templateName: string): QuickHelpItem[] => {
    return TEMPLATE_HINTS[templateName?.toLowerCase()] || []
}

/**
 * Add custom hints for a template
 */
export const addTemplateHints = (
    templateName: string,
    hints: QuickHelpItem[]
): void => {
    TEMPLATE_HINTS[templateName.toLowerCase()] = [
        ...(TEMPLATE_HINTS[templateName.toLowerCase()] || []),
        ...hints,
    ]
}
